// =====================================================
// R-SIS-1 — Jalali (Shamsi) ⇄ Gregorian conversion + calendar math.
//
// The SIS stores all dates as Gregorian ISO (like every other model);
// the UI lets users read/enter them in Jalali. `i18n/format.js` already
// formats Gregorian→Jalali for *display*; this module adds the inverse
// (so a JalaliDatePicker can turn a user's picked Shamsi date into an ISO
// string) plus the calendar arithmetic a month-grid needs (leap years +
// month lengths). Standard fallback algorithm — no ICU dependency.
// =====================================================

export interface JalaliParts {
  jy: number;
  jm: number; // 1..12
  jd: number; // 1..31
}

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

export const toFaDigits = (input: string | number): string =>
  String(input ?? "").replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]);

export const toEnDigits = (input: string): string =>
  String(input ?? "").replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)));

export const JALALI_MONTHS_FA = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
] as const;

// Persian week starts Saturday. getDay(): 0=Sun..6=Sat → Saturday index 0.
export const JALALI_WEEKDAYS_FA = ["ش", "ی", "د", "س", "چ", "پ", "ج"] as const;

/**
 * Jalali leap-year test, derived from the SAME G↔J conversion the rest of this
 * module uses: a year is leap iff the Gregorian span from its Nowruz to the
 * next Nowruz is 366 days. This guarantees leap status, Esfand length and date
 * round-trips can never disagree. (The previous Birashk 33-year arithmetic
 * disagreed with the conversion for years such as 1404/1408 — it wrongly
 * reported them leap, which would let a picker offer the non-existent «۳۰ اسفند».)
 */
export const jalaliIsLeap = (jy: number): boolean => {
  const a = jalaliToGregorian(jy, 1, 1);
  const b = jalaliToGregorian(jy + 1, 1, 1);
  const days = Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86_400_000);
  return days === 366;
};

/** Days in a given Jalali month (1..12). Esfand = 29, or 30 in a leap year. */
export const jalaliMonthLength = (jy: number, jm: number): number => {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return jalaliIsLeap(jy) ? 30 : 29;
};

/** Gregorian → Jalali. gm/gd are 1-based. */
export const gregorianToJalali = (gy: number, gm: number, gd: number): JalaliParts => {
  const gDaysInMonth = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy = gy <= 1600 ? 0 : 979;
  let gy2 = gy <= 1600 ? gy - 621 : gy - 1600;
  const gyForLeap = gm > 2 ? gy2 + 1 : gy2;
  let days =
    365 * gy2 +
    Math.floor((gyForLeap + 3) / 4) -
    Math.floor((gyForLeap + 99) / 100) +
    Math.floor((gyForLeap + 399) / 400) -
    80 +
    gd +
    gDaysInMonth[gm - 1];
  jy += 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
};

/** Jalali → Gregorian. Returns 1-based [gy, gm, gd]. */
export const jalaliToGregorian = (jy: number, jm: number, jd: number): [number, number, number] => {
  let gy = jy <= 979 ? 621 : 1600;
  const jy2 = jy <= 979 ? jy : jy - 979;
  let days =
    365 * jy2 +
    Math.floor(jy2 / 33) * 8 +
    Math.floor(((jy2 % 33) + 3) / 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  gy += 400 * Math.floor(days / 146097);
  days %= 146097;
  if (days > 36524) {
    gy += 100 * Math.floor(--days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }
  gy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  let gd = days + 1;
  const isGregLeap = (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;
  const gMonthLen = [31, isGregLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let gm = 0;
  while (gm < 12 && gd > gMonthLen[gm]) {
    gd -= gMonthLen[gm];
    gm++;
  }
  return [gy, gm + 1, gd];
};

/** A JS Date (local) → Jalali parts. */
export const dateToJalali = (d: Date): JalaliParts =>
  gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());

/**
 * Jalali parts → a JS Date at local midnight. We build it via the
 * Gregorian Y/M/D so the calendar day is exact regardless of timezone.
 */
export const jalaliToDate = (jy: number, jm: number, jd: number): Date => {
  const [gy, gm, gd] = jalaliToGregorian(jy, jm, jd);
  return new Date(gy, gm - 1, gd);
};

/** ISO datetime string (UTC midnight of the picked day) for API payloads. */
export const jalaliToIso = (jy: number, jm: number, jd: number): string => {
  const [gy, gm, gd] = jalaliToGregorian(jy, jm, jd);
  // Anchor at UTC midnight so the stored instant matches the picked day
  // everywhere (term windows are day-granular).
  return new Date(Date.UTC(gy, gm - 1, gd)).toISOString();
};

/** "۱۴۰۵/۰۶/۳۱" from a Date (or "" if null/invalid). */
export const formatJalaliShort = (d: Date | string | null | undefined): string => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "";
  const { jy, jm, jd } = dateToJalali(date);
  const pad = (n: number) => String(n).padStart(2, "0");
  return toFaDigits(`${jy}/${pad(jm)}/${pad(jd)}`);
};
